// Looks + Look Detail + Share — TRIMIE Hi-Fi v1 (per docs/design-spec.md §4.10–4.12)
//
// Grid view: chip rail (All / Favorites / This week), 2-col grid of look cards,
// each foot = mono date + heart toggle. Filter icon in header.
//
// Detail view: full-bleed hero, mono caption, action row (Saved / Try this look /
// Share). Share opens the §4.12 bottom sheet.

import { useState, useEffect, useRef, useCallback } from 'react';
import api from './api';
import Header from './components/Header';
import EmptyState from './components/EmptyState';
import { resolveUrl } from './utils/media';

const POLL_INTERVAL = 5000;
const FAV_KEY = 'trimie_look_favs';

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const loadFavs = () => {
  try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveFavs = (set) => {
  try { localStorage.setItem(FAV_KEY, JSON.stringify([...set])); }
  catch { /* quota */ }
};

const FilterIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 5h18M6 12h12M10 19h4" />
  </svg>
);

const ShareGlyph = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);

function Heart({ filled, size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
         fill={filled ? 'currentColor' : 'none'}
         stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

// ── Share bottom sheet (§4.12) ─────────────────────────────────────────────
function ShareSheet({ item, defaultAsset, onClose }) {
  const imageUrl = resolveUrl(item.tryon_image_url);
  const videoUrl = resolveUrl(item.first_video_url);
  const hasVideo = !!videoUrl;
  const hasImage = !!imageUrl;

  // Default to whatever the caller wanted; fall back to whatever exists.
  const [assetType, setAssetType] = useState(() => {
    if (defaultAsset === 'video' && hasVideo) return 'video';
    if (defaultAsset === 'image' && hasImage) return 'image';
    return hasVideo ? 'video' : 'image';
  });

  const url = assetType === 'video' ? videoUrl : imageUrl;
  const shareTitle = assetType === 'video' ? 'My TRIMIE video' : 'My TRIMIE look';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  const handleMore = async () => {
    if (!url) return;
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, url }); } catch { /* user cancel */ }
    } else {
      handleCopy();
    }
  };

  const tiles = [
    { key: 'msg',     label: 'Messages',  onClick: handleMore,
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 12a8 8 0 1 1-3.5-6.6L21 4l-1.4 3.4A8 8 0 0 1 21 12z" /></svg> },
    { key: 'ig',      label: 'Instagram', onClick: handleMore,
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" /></svg> },
    { key: 'tt',      label: 'TikTok',    onClick: handleMore,
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16 3v3.4a5.6 5.6 0 0 0 4 1.6V11a8.4 8.4 0 0 1-4-1v6a5 5 0 1 1-5-5v3a2 2 0 1 0 2 2V3h3z" /></svg> },
    { key: 'copy',    label: copied ? 'Copied!' : 'Copy link', onClick: handleCopy,
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg> },
    { key: 'more',    label: 'More',      onClick: handleMore,
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="18" cy="12" r="1.6" /></svg> },
  ];

  return (
    <div className="t-sheet-backdrop" onClick={onClose}>
      <div className="t-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="t-sheet-handle" />
        <div className="t-sheet-title">Share to</div>

        {/* Asset-type toggle — only render when both image and video exist. */}
        {hasImage && hasVideo && (
          <div className="share-asset-toggle" role="tablist" aria-label="Choose what to share">
            <button
              type="button"
              role="tab"
              aria-selected={assetType === 'image'}
              className={`share-asset-pill${assetType === 'image' ? ' active' : ''}`}
              onClick={() => setAssetType('image')}
            >
              Image
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={assetType === 'video'}
              className={`share-asset-pill${assetType === 'video' ? ' active' : ''}`}
              onClick={() => setAssetType('video')}
            >
              Video
            </button>
          </div>
        )}

        <div className="share-tiles">
          {tiles.map((t) => (
            <button
              key={t.key}
              type="button"
              className="share-tile"
              onClick={t.onClick}
              disabled={!url}
            >
              <span className="share-tile-icon">{t.icon}</span>
              <span className="share-tile-label">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="poll-friends">
          <div className="poll-friends-body">
            <div className="poll-friends-title">Poll friends</div>
            <div className="poll-friends-sub">Let friends pick between 2–4 looks</div>
          </div>
          <button className="poll-friends-go" type="button" disabled>Soon</button>
        </div>
      </div>
    </div>
  );
}

// ── Detail view (§4.11) ────────────────────────────────────────────────────
const VIDEO_TIMEOUT_MS = 3 * 60 * 1000;

function DetailView({ item, isFav, onToggleFav, onBack, onNav }) {
  const [videoStep, setVideoStep] = useState(() => item.first_video_url ? 'done' : 'idle');
  const [videoUrl, setVideoUrl]   = useState(() => resolveUrl(item.first_video_url) || null);
  const [videoError, setVideoError] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoElapsed, setVideoElapsed] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(elapsedRef.current);
  }, []);

  const stopTimers = useCallback(() => {
    clearInterval(pollRef.current);
    clearInterval(elapsedRef.current);
  }, []);

  const handleCancelVideo = useCallback(() => {
    stopTimers();
    setVideoStep('idle');
    setVideoStatus(null);
    setVideoElapsed(0);
  }, [stopTimers]);

  const handleGenerateVideo = useCallback(async () => {
    setVideoStep('polling');
    setVideoError(null);
    setVideoStatus('submitting');
    setVideoElapsed(0);
    const startedAt = Date.now();
    elapsedRef.current = setInterval(() => {
      setVideoElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    try {
      const res = await api.post('/tryon/video/', { tryon_id: item.task_id });
      const videoTaskId = res.data?.data?.task_id || res.data?.data?.video_task_id;
      if (!videoTaskId) throw new Error('No video task ID');
      pollRef.current = setInterval(async () => {
        if (Date.now() - startedAt > VIDEO_TIMEOUT_MS) {
          stopTimers();
          setVideoError('Video took longer than 3 minutes. Kling may be queued or out of credits.');
          setVideoStep('error');
          return;
        }
        try {
          const r = await api.get(`/tryon/video/${videoTaskId}/`);
          const st  = r.data?.data?.status;
          const url = r.data?.data?.stored_video_url;
          setVideoStatus(st);
          if (st === 'completed') {
            stopTimers();
            if (url) {
              setVideoUrl(resolveUrl(url));
              setVideoStep('done');
            } else {
              setVideoError('Video finished but no URL was returned.');
              setVideoStep('error');
            }
          } else if (st === 'failed') {
            stopTimers();
            setVideoError(r.data?.data?.error_detail || 'Video failed.');
            setVideoStep('error');
          }
        } catch {
          stopTimers();
          setVideoStep('error');
        }
      }, POLL_INTERVAL);
    } catch (e) {
      stopTimers();
      setVideoError(e.response?.data?.message || e.message);
      setVideoStep('error');
    }
  }, [item.task_id, stopTimers]);

  const imgUrl = resolveUrl(item.tryon_image_url);

  return (
    <div className="screen">
      <Header
        title="Look"
        onBack={onBack}
        action={{ icon: ShareGlyph, onClick: () => setShareOpen(true), ariaLabel: 'Share' }}
      />

      <div className="look-detail">
        <div className="look-detail-hero">
          {imgUrl && <img src={imgUrl} alt="Your look" />}
        </div>

        <div className="mono-eyebrow look-detail-caption">
          {formatDate(item.created_at)} · TRIMIE Look
        </div>

        <div className="look-detail-actions">
          <button className={`btn-outline${isFav ? ' is-fav' : ''}`} onClick={onToggleFav}>
            <Heart filled={isFav} /> {isFav ? 'Saved' : 'Save'}
          </button>
          <button className="btn-purple" onClick={() => onNav('tryon', { modelPhotoUrl: item.model_photo_url })}>
            Try this look
          </button>
          <button className="btn-outline" onClick={() => setShareOpen(true)}>
            Share
          </button>
        </div>

        {/* ── Video section: 'See it move' ──────────────────────────────── */}
        {videoStep === 'idle' && (
          <button className="btn-purple" onClick={handleGenerateVideo}>
            See it move (generates ~60s)
          </button>
        )}
        {videoStep === 'polling' && (
          <div className="tryon-status">
            <span className="spinner" />
            <span>
              Kling: <strong>{videoStatus || 'submitting'}</strong> · {videoElapsed}s elapsed
              {' '}
              <button type="button" className="link-inline" onClick={handleCancelVideo}>Cancel</button>
            </span>
          </div>
        )}
        {videoStep === 'error' && (
          <div className="alert error">
            Video failed{videoError ? `: ${videoError}` : '.'}{' '}
            <button type="button" className="link-inline" onClick={() => { setVideoStep('idle'); setVideoError(null); }}>
              Try again
            </button>
          </div>
        )}
        {videoStep === 'done' && videoUrl && (
          <div className="tryon-result-video">
            <video src={videoUrl} autoPlay loop playsInline controls />
          </div>
        )}
      </div>

      {shareOpen && (
        <ShareSheet
          item={item}
          defaultAsset={videoStep === 'done' && videoUrl ? 'video' : 'image'}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

// ── Grid view (§4.10) ──────────────────────────────────────────────────────
const CHIP_FILTERS = [
  { key: 'all',  label: 'All',       match: () => true },
  { key: 'fav',  label: 'Favorites', match: (item, favs) => favs.has(item.task_id) },
  { key: 'week', label: 'This week', match: (item) => {
    if (!item.created_at) return false;
    const d = new Date(item.created_at).getTime();
    return Date.now() - d < 7 * 24 * 60 * 60 * 1000;
  }},
];

export default function MyLooksPage({ onNav }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeChip, setActiveChip] = useState('all');
  const [favs, setFavs]       = useState(loadFavs);

  useEffect(() => {
    api.get('/tryon/image/', { params: { status: 'completed' } })
      .then(res => {
        const data = res.data?.data?.results ?? res.data?.data ?? [];
        setItems(Array.isArray(data) ? data.filter(t => t.tryon_image_url) : []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const toggleFav = (taskId) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      saveFavs(next);
      return next;
    });
  };

  if (selected) {
    return (
      <DetailView
        item={selected}
        isFav={favs.has(selected.task_id)}
        onToggleFav={() => toggleFav(selected.task_id)}
        onBack={() => setSelected(null)}
        onNav={onNav}
      />
    );
  }

  const activeFilter = CHIP_FILTERS.find(c => c.key === activeChip) || CHIP_FILTERS[0];
  const visible = items.filter(it => activeFilter.match(it, favs));

  return (
    <div className="screen">
      <Header
        title="Looks"
        action={{ icon: FilterIcon, ariaLabel: 'Filter looks' }}
      />

      {!loading && items.length > 0 && (
        <div className="looks-chips">
          {CHIP_FILTERS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`looks-chip${activeChip === c.key ? ' active' : ''}`}
              onClick={() => setActiveChip(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <span className="spinner" style={{ color: 'var(--t-purple)', width: 28, height: 28, borderWidth: 3 }} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          iconShape="rounded"
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--t-purple)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
            </svg>
          }
          title="No looks yet"
          body="Generate your first try-on to start building your looks library."
          actions={
            <>
              <button className="btn-purple" onClick={() => onNav('tryon')}>Try something on</button>
              <button className="btn-outline" onClick={() => onNav('closet')}>Browse closet</button>
            </>
          }
        />
      ) : visible.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t-muted)', fontSize: 13 }}>
          Nothing in {activeFilter.label.toLowerCase()} yet.
        </div>
      ) : (
        <div className="looks-grid">
          {visible.map(item => {
            const imgUrl = resolveUrl(item.tryon_image_url);
            const hasVideo = !!item.first_video_url;
            const fav = favs.has(item.task_id);
            return (
              <div
                key={item.id}
                className="look-card"
                onClick={() => setSelected(item)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                <div className="look-card-img">
                  {imgUrl
                    ? <img src={imgUrl} alt="Look" />
                    : <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.2"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>}
                  {hasVideo && (
                    <span className="look-card-360">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21" /></svg>
                      360°
                    </span>
                  )}
                </div>
                <div className="look-card-foot">
                  <span className="look-card-title">{formatDate(item.created_at)}</span>
                  <button
                    type="button"
                    className={`look-card-heart${fav ? ' active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleFav(item.task_id); }}
                    aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Heart filled={fav} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
